import React, { useState, useEffect, memo } from 'react';
import { Layout } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import {
  FileTextOutlined,
  BookOutlined,
  LeftOutlined,
  TagsOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { AiOutlineApartment } from "react-icons/ai";
import { FaUsers, FaUsersCog, FaLaptopHouse, FaFileAlt } from "react-icons/fa";
import logoUDI from '../assets/img/logo.png';
import { usePermissions, ROLES } from '../hooks/usePermissions';
import './Sidebar.css';

const { Sider } = Layout;

const Sidebar = memo(({ collapsed, toggleCollapsed }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { isAdmin, canManageData, isAuthenticated } = usePermissions();
  
  // Get current active menu item
  const currentKey = currentPath.split('/')[1] || 'novedades';
  
  // Definimos menuItems como useMemo para evitar recálculos en re-renders
  const menuItems = React.useMemo(() => {
    const items = [
      {
        key: 'dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
        path: '/dashboard',
        roles: [ROLES.ADMINISTRADOR, ROLES.ADMINISTRATIVO, ROLES.AUDITOR, ROLES.COORDINADOR] // Todos pueden ver
      },
      {
        key: 'novedades',
        icon: <FileTextOutlined />,
        label: 'Novedades',
        path: '/novedades',
        roles: [ROLES.ADMINISTRADOR, ROLES.ADMINISTRATIVO, ROLES.AUDITOR, ROLES.COORDINADOR] // Todos pueden ver
      }
    ];

    // Solo administradores pueden gestionar usuarios
    if (isAdmin()) {
      items.push({
        key: 'usuarios',
        icon: <FaUsers />,
        label: 'Usuarios',
        path: '/usuarios',
        roles: [ROLES.ADMINISTRADOR]
      });
    }

    // Solo administradores pueden gestionar empleados
    if (isAdmin()) {
      items.push({
        key: 'empleados',
        icon: <FaUsersCog />,
        label: 'Empleados',
        path: '/empleados',
        roles: [ROLES.ADMINISTRADOR]
      });
    }

    // Solo administradores pueden gestionar campus
    if (isAdmin()) {
      items.push({
        key: 'campus',
        icon: <FaLaptopHouse />,
        label: 'Campus',
        path: '/campus',
        roles: [ROLES.ADMINISTRADOR]
      });
    }

    // Solo administradores pueden gestionar catálogos
    if (isAdmin()) {
      items.push(
        {
          key: 'dependencias',
          icon: <AiOutlineApartment />,
          label: 'Dependencias',
          path: '/dependencias',
          roles: [ROLES.ADMINISTRADOR]
        },
        {
          key: 'tipos-contratos',
          icon: <FaFileAlt />,
          label: 'Tipos de Contratos',
          path: '/tipos-contratos',
          roles: [ROLES.ADMINISTRADOR]
        },
        {
          key: 'tipos-novedades',
          icon: <TagsOutlined />,
          label: 'Tipos de Novedades',
          path: '/tipos-novedades',
          roles: [ROLES.ADMINISTRADOR]
        }
      );
    }

    return items;
  }, [isAdmin, canManageData]);

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      className="h-screen sidebar-component"
      theme="light"
      width={220}
      style={{
        background: '#FFFFFF',
        borderRight: '1px solid #f0f0f0',
        overflow: 'auto',
        height: '100vh',
        zIndex: 1000,
      }}
    >
      {/* Logo Section */}
      <div className="py-4 flex justify-between items-center px-5 border-b border-gray-100">
        {!collapsed ? (
          <>
            <div className="flex items-center">
              <img src={logoUDI} alt="Logo UDI" className="h-8 mr-3" />
              <span className="font-semibold text-lg text-gray-800">UDI</span>
            </div>
            <button
              onClick={toggleCollapsed}
              className="text-gray-400 hover:text-gray-600"
            >
              <LeftOutlined />
            </button>
          </>
        ) : (
          <div className="w-full flex justify-center">
            <img src={logoUDI} alt="Logo UDI" className="h-8 w-8 object-contain" />
          </div>
        )}
      </div>

      {/* Menu Categories */}
      <div className="mt-4 mb-2 px-4">
        <p className="text-xs font-medium uppercase text-gray-400">
          {collapsed ? '' : 'Menu'}
        </p>
      </div>

      {/* Menu Items */}
      <div className="px-2">
        {menuItems.map((item) => {
          const isActive = currentKey === item.key;
          return (
            <Link to={item.path} key={item.key}>
              <div 
                className={`flex items-center px-3 py-2.5 rounded-md mb-1 transition-all ${
                  isActive 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className={`text-lg ${isActive ? 'text-blue-500' : 'text-gray-400'}`}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="ml-3 text-sm font-medium">
                    {item.label}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Collapse Toggle Button for Mobile */}
      {collapsed && (
        <div className="absolute bottom-4 w-full text-center">
          <button
            onClick={toggleCollapsed}
            className="flex mx-auto items-center justify-center w-8 h-8 border rounded-full hover:bg-gray-100 transition-all"
          >
            <LeftOutlined style={{ transform: 'rotate(180deg)' }} />
          </button>
        </div>
      )}
    </Sider>
  );
});

export default Sidebar;